<style lang="scss" src="./style.scss"></style>

<template>
  <div>
    <div :class="['multi-select', {'multi-select--open': open}]">
      <div class="multi-select__label">
        <v-button
          icon-right="angle-down"
          class="btn--hollow"
          @click="toggleDropdown"
        >
          <span
            v-if="selectedItems.length < 1"
          >
            Select...
          </span>
          <span
            v-else
          >
            {{ toggleLabel }}
          </span>
        </v-button>
      </div>

      <transition name="vs__fade">
        <div
          v-if="open"
          v-clickaway="closeDropdown"
          class="multi-select__dropdown"
        >
          <div
            v-if="hasFilter"
            class="multi-select__searchbox"
          >
            <v-input
              v-model="searchQuery"
              type="text"
              placeholder="Search"
              :autocomplete="false"
            />
          </div>

          <div class="multi-select__content">
            <div
              v-for="(item, index) in visibleItems"
              :key="index"
              class="multi-select__item"
              :class="[columnAmountClass]"
            >
              <label class="multi-select__item-label">
                <input
                  v-model="selectedValues"
                  :value="itemValue(item)"
                  type="checkbox"
                  class="multi-select__item-checkbox"
                  :disabled="!emptyAllowed && selectedValues.length === 1 && itemIsSelected(item)"
                >
                <slot
                  name="label"
                  :item="item"
                >
                  <span>{{ label(item) }}</span>
                </slot>
              </label>
            </div>
          </div>

          <div
            v-if="hasFooter"
            class="multi-select__footer"
          >
            <v-button
              class="multi-select__footer-button-select-all text-secondary"
              @click="handleSelectAll"
            >
              Select all
            </v-button>
            <v-button
              class="btn--secondary"
              @click="closeDropdown"
            >
              Done
            </v-button>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VMultiSelect',
  props: {
    items: {
      type: Array,
      default() {
        return [];
      },
    },
    labelAttribute: {
      type: String,
      default: 'label',
    },
    valueAttribute: {
      type: String,
      default: 'value',
    },
    labelItemAmount: {
      type: Number,
      default: 4,
    },
    columnAmount: {
      type: Number,
      default: 3,
    },
    hasFilter: {
      type: Boolean,
      default: false,
    },
    hasFooter: {
      type: Boolean,
      default: false,
    },
    initialSelectedItems: {
      type: Array,
      default() {
        return [];
      },
    },
    emptyAllowed: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      open: false,
      searchQuery: '',
      visibleItems: [],
      selectedItems: [],
      selectedValues: [],
    };
  },
  computed: {
    allItemsAreSelected() {
      return this.items.length === this.selectedItems.length;
    },
    toggleLabel() {
      const shownItems = this.selectedItems.slice(0, this.labelItemAmount);
      const label = shownItems.map((item) => this.label(item)).join(', ');

      const itemsLeft = this.selectedItems.length - shownItems.length;
      if (itemsLeft > 0) {
        return label.concat(` and ${itemsLeft} more`);
      }

      return label;
    },
    columnAmountClass() {
      return `multi-select__item--columns-${this.columnAmount}`;
    },
  },
  watch: {
    searchQuery() {
      this.filterItems();
    },
    items: {
      handler() {
        this.visibleItems = this.items;
      },
      immediate: true,
    },
    initialSelectedItems() {
      this.selectedItems = this.initialSelectedItems;
    },
    selectedItems(current, old) {
      const oldValues = this.itemValues(old);
      const currentValues = this.itemValues(current);
      if (!this.arrayEquals(oldValues, currentValues)) {
        this.selectedValues = currentValues;
        this.$emit('change', this.selectedItems);
      }
    },
    selectedValues(current, old) {
      if (!this.arrayEquals(old, current)) {
        this.selectedItems = this.items.filter((item) => this.selectedValues.includes(this.itemValue(item)));
      }
    },
  },
  methods: {
    arrayEquals(a, b) {
      const sortedB = [...b].sort();
      return a.length === b.length && [...a].sort().every((value, index) => { return value === sortedB[index];});
    },
    toggleDropdown() {
      this.open = !this.open;
    },
    closeDropdown() {
      this.open = false;
    },
    handleSelectAll() {
      this.toggleAll(!this.allItemsAreSelected);
    },
    toggleAll(select) {
      this.selectedItems = select ? this.items : [];
    },
    label(item) {
      return item[this.labelAttribute];
    },
    itemValues(items) {
      return items.map((item) => this.itemValue(item));
    },
    itemValue(item) {
      return item[this.valueAttribute];
    },
    itemIsSelected(item) {
      return this.selectedItems.find((selectedItem) => this.itemValue(selectedItem) === this.itemValue(item)) !== undefined;
    },
    filterItems() {
      this.visibleItems = this.items.filter((item) => {
        const query = this.searchQuery.toLowerCase();
        const searchAttributes = ['label', 'name', 'value'].concat(this.labelAttribute);

        return searchAttributes.some((attribute) => {
          if (item[attribute] === undefined) {
            return false;
          }

          return item[attribute].toLowerCase().indexOf(query) >= 0;
        });
      });
    },
  },
};
</script>
